import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { CommandCell } from '../../forms/CommandCell';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { GetNextSaturday } from '../../../app/DateFunctions';

import './VenueEvent.scss';

export const AdministratorVenueEventTemporarilyHiddenFromReports = (props) => {
    const dispatch = useDispatch();

    const editField = "inEdit";

    const defaultReport = {
        reportId: "",
        name: "Select Report",
    };

    const [venueEventTemporarilyHiddenFromReports, setVenueEventTemporarilyHiddenFromReports] = useState([]);
    const [areVenueEventTemporarilyHiddenFromReportsLoading, setAreVenueEventTemporarilyHiddenFromReportsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState("");
    const [selectedReport, setSelectedReport] = useState(defaultReport);
    const [defaultStopHidingDate, setDefaultStopHidingDate] = useState(undefined);
    const [selectedStopHidingDate, setSelectedStopHidingDate] = useState(undefined);
    const [reports, setReports] = useState(undefined);    

    const getVenueEventTemporarilyHiddenFromReports = () => {
        setAreVenueEventTemporarilyHiddenFromReportsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
        };

        fetch(`/api/administrator/venueeventtemporarilyhiddenfromreports`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setVenueEventTemporarilyHiddenFromReports(result);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setLoadingError(error.message);
            })
            .finally(() => {
                setAreVenueEventTemporarilyHiddenFromReportsLoading(false);
            });
    }

    const deleteVenueEventHiddenFromReport = (venueEventTemporarilyHiddenFromReportKey) => {
        setAreVenueEventTemporarilyHiddenFromReportsLoading(true);

        const dataItem = {
            venueEventTemporarilyHiddenFromReportKey: venueEventTemporarilyHiddenFromReportKey
        }

        fetch("/api/administrator/venueeventtemporarilyhiddenfromreport",
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Deleted item successfully");

                getVenueEventTemporarilyHiddenFromReports();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventTemporarilyHiddenFromReportsLoading(false);
            });
    }

    const hideEventFromReport = () => {
        const reportId = selectedReport.reportId;

        if (reportId === "") {
            addNotificationWithTimeout(dispatch, "error", "You must select a report");
            return;
        }

        if (!selectedStopHidingDate) {
            addNotificationWithTimeout(dispatch, "error", "You must select a stop hiding date");
            return;
        }

        const day = selectedStopHidingDate.getDate();
        const month = selectedStopHidingDate.getMonth() + 1;
        const year = selectedStopHidingDate.getFullYear();
        const stopHidingDate = `${month}/${day}/${year}`;

        setAreVenueEventTemporarilyHiddenFromReportsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
            reportId: reportId,
            stopHidingDate: stopHidingDate,
        }

        fetch("/api/administrator/venueeventtemporarilyhiddenfromreport",
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Temporarily hid event from report");

                getVenueEventTemporarilyHiddenFromReports();

                setSelectedReport(defaultReport);
                setSelectedStopHidingDate(defaultStopHidingDate);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setAreVenueEventTemporarilyHiddenFromReportsLoading(false);
            });
    }

    const discard = (dataItem) => {
        const newVenueEventTemporarilyHiddenFromReports = [...venueEventTemporarilyHiddenFromReports];
        newVenueEventTemporarilyHiddenFromReports.splice(0, 1);
        setVenueEventTemporarilyHiddenFromReports(newVenueEventTemporarilyHiddenFromReports);
    };

    const remove = (dataItem) => {
        deleteVenueEventHiddenFromReport(dataItem.venueEventTemporarilyHiddenFromReportKey);
    };

    const MyCommandCell = (props) => (
        <CommandCell
            {...props}
            canEdit={false}
            remove={remove}
            discard={discard}
            editField={editField}
            keyField="venueEventTemporarilyHiddenFromReportKey"
        />
    );

    const getReports = () => {
        fetch(`/api/general/reports`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify({}),
            })
            .then(handleResponse)
            .then(result => {
                const allReports = [defaultReport].concat(result);
                setReports(allReports);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const reportChanged = (event) => {
        setSelectedReport(event.target.value);
    };

    const stopHidingDateChanged = (event) => {
        setSelectedStopHidingDate(event.target.value);
    };

    useEffect(() => {
        if (props.venueEventKey) {
            getReports();
            getVenueEventTemporarilyHiddenFromReports();

            const nextSaturday = GetNextSaturday();
            setDefaultStopHidingDate(nextSaturday);
            setSelectedStopHidingDate(nextSaturday);
        }
    }, []);

    return (
        <>
            {areVenueEventTemporarilyHiddenFromReportsLoading ? <LoadingPanel /> : (loadingError === "" ? (
                <>
                    <div className="row mb-3 mt-3">
                        <div className="col-md-4">
                            <DropDownList
                                id="report"
                                textField="name"
                                dataItemKey="reportId"
                                data={reports}
                                value={selectedReport}
                                onChange={reportChanged}
                            />
                        </div>
                        <div className="col-md-4">
                            <DatePicker
                                id="stopHidingDate"
                                placeholder="Stop hiding date"
                                value={selectedStopHidingDate}
                                onChange={stopHidingDateChanged}
                            />
                        </div>
                        <div className="col-md-4">
                            <Button
                                themeColor={"primary"}
                                type={"button"}
                                onClick={hideEventFromReport}
                            >
                                Temporarily Hide Event
                            </Button>
                        </div>
                    </div>

                    <Grid
                        data={venueEventTemporarilyHiddenFromReports}
                        editField={editField}
                        dataItemKey={"venueEventTemporarilyHiddenFromReportKey"}
                        resizable={true}
                        scrollable="none"
                    >
                        <Column field="reportName" title={"Report"} />
                        <Column field="loginName" title={"Login Name"} />
                        <Column field="stopHidingDate" title={"Stop Hiding"} />
                        <Column field="dateCreated" title={"Date Hidden"} />
                        <Column cell={MyCommandCell} width="150px" />
                    </Grid>
                </>
            ) : <></>)
            }
        </>
    );
}